import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useGetBranchByIdQuery } from "../../../api/branchApi";
import { useGetCompanyByIdQuery } from "../../../api/companyApi";
import { useGetUsersByBranchIdQuery } from "../../../api/userApi";
import { useGetReviewsQuery, useGetMonthReviewsQuery } from "../../../api/reviewApi";
import { RatingResults } from "./ratingResults";
import { LoadingIndicator } from "../../shared/alerts/loadingIndicator";
import { ErrorDisplay } from "../../shared/alerts/errorDisplay";
import { User } from "../../../interfaces/entities/user";
import { Review } from "../../../interfaces/entities/review";
import { TOP_PERFORMER_MONTH } from "../../../utils/config";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";

interface FilterState {
  selectedMonth: string;
  selectedYear: string;
  selectedEmployeeId: string;
  selectedStars: string;
}

interface TopPerformer {
  user: User;
  averageRating: number;
  totalReviews: number;
}

// Helper function to get month name + year based on offset from current month
const getMonthName = (monthOffset: number): string => {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  return `${monthNames[target.getMonth()]} ${target.getFullYear()}`;
};

const getMonthNameByNumber = (monthNumber: string): string => {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const index = Number(monthNumber) - 1;
  return monthNames[index] || "Selected Month";
};

export const RatingResultsContainer: React.FC = () => {
  const { branchId, companyId } = useParams<{ branchId?: string; companyId?: string }>();
  const [filters, setFilters] = useState<FilterState>({
    selectedMonth: "",
    selectedYear: "",
    selectedEmployeeId: "",
    selectedStars: "",
  });

  const { data: branch, isLoading: loadingBranch, error: errorBranch } = useGetBranchByIdQuery(branchId ?? "");
  const { data: company } = useGetCompanyByIdQuery(companyId ?? '', { skip: !companyId });
  const { data: employees = [], isLoading: loadingEmployees, error: errorEmployees } = useGetUsersByBranchIdQuery({ branchId }, {
    skip: !branchId,
  });

  const { currentUserIsOwnerOf } = useAuthenticatedUser();

  // Build filters for reviews query
  const reviewFilters = useMemo(() => {
    const f: {
      branchId?: string;
      userId?: string;
      fromDate?: string;
      toDate?: string;
    } = {};

    if (branchId) {
      f.branchId = branchId;
    }

    if (filters.selectedEmployeeId) {
      f.userId = filters.selectedEmployeeId;
    }

    if (filters.selectedYear) {
      const selectedYear = Number(filters.selectedYear);

      if (filters.selectedMonth) {
        const selectedMonth = Number(filters.selectedMonth);
        const monthText = String(selectedMonth).padStart(2, "0");
        const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();

        f.fromDate = `${selectedYear}-${monthText}-01`;
        f.toDate = `${selectedYear}-${monthText}-${String(lastDay).padStart(2, "0")}`;
      } else {
        f.fromDate = `${selectedYear}-01-01`;
        f.toDate = `${selectedYear}-12-31`;
      }
    }

    return f;
  }, [branchId, filters]);

  const {
    data: reviews = [],
    isLoading: loadingReviews,
    error: errorReviews,
    refetch,
  } = useGetReviewsQuery(reviewFilters);

  // Determine month offset based on config
  const monthOffset = TOP_PERFORMER_MONTH === 'previous' ? -1 : 0;

  // Single API call with month type parameter
  const { data: monthReviews = [], isLoading: loadingMonthReviews } = useGetMonthReviewsQuery(
    { 
      branchId: branchId ?? "", 
      companyId,
      monthType: TOP_PERFORMER_MONTH 
    },
    { skip: !branchId }
  );


  const handleFilterChange = (name: keyof FilterState, value: string) => {
    setFilters(prev => {
      if (name === "selectedYear" && !value) {
        return {
          ...prev,
          selectedYear: "",
          selectedMonth: "",
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
    // Refetch data when filters change
    refetch();
  };

  const clearFilters = () => {
    setFilters({
      selectedMonth: "",
      selectedYear: "",
      selectedEmployeeId: "",
      selectedStars: "",
    });
    refetch();
  };

  const filteredReviews = useMemo(() => {
    if (!filters.selectedStars) {
      return reviews;
    }

    const selectedStars = Number(filters.selectedStars);
    return reviews.filter((review) => (review.rating || 0) === selectedStars);
  }, [reviews, filters.selectedStars]);

  // Calculate average rating (based on filtered reviews)
  const averageRating = useMemo(() => {
    if (filteredReviews.length === 0) return 0;
    const sum = filteredReviews.reduce((acc, review) => acc + (review.rating || 0), 0);
    return sum / filteredReviews.length;
  }, [filteredReviews]);

  const totalReviews = filteredReviews.length;

  const employeeSummary = useMemo(() => {
    const map: Record<string, { sum: number; count: number }> = {};
    filteredReviews.forEach(r => {
      if (!r.userId) return;
      if (!map[r.userId]) map[r.userId] = { sum: 0, count: 0 };
      map[r.userId].sum += r.rating || 0;
      map[r.userId].count += 1;
    });
    return Object.entries(map)
      .map(([userId, { sum, count }]) => {
        const emp = employees.find(e => e.id === userId);
        return {
          userId,
          empNo: emp?.empNo || '',
          name: emp ? `${emp.firstName} ${emp.lastName || ''}`.trim() : 'Unknown',
          averageRating: sum / count,
          totalReviews: count,
        };
      })
      .sort((a, b) => b.averageRating - a.averageRating);
  }, [filteredReviews, employees]);

  const hasSelectedPeriod = Boolean(filters.selectedYear || filters.selectedMonth);

  const topPerformerOfSelectedPeriod = useMemo((): TopPerformer | null => {
    if (!hasSelectedPeriod || reviews.length === 0) {
      return null;
    }

    const employeeRatings: Record<string, { sum: number; count: number }> = {};

    reviews.forEach((review: Review) => {
      if (!review.userId) return;
      if (!employeeRatings[review.userId]) {
        employeeRatings[review.userId] = { sum: 0, count: 0 };
      }

      employeeRatings[review.userId].sum += review.rating || 0;
      employeeRatings[review.userId].count += 1;
    });

    let topEmployeeId: string | null = null;
    let highestAverage = 0;

    Object.entries(employeeRatings).forEach(([userId, data]) => {
      const avg = data.sum / data.count;
      if (avg > highestAverage) {
        highestAverage = avg;
        topEmployeeId = userId;
      }
    });

    if (!topEmployeeId) return null;

    const topUser = employees.find((e) => e.id === topEmployeeId);
    if (!topUser) return null;

    return {
      user: topUser,
      averageRating: highestAverage,
      totalReviews: employeeRatings[topEmployeeId].count,
    };
  }, [hasSelectedPeriod, reviews, employees]);

  // Calculate top performer of the configured month (using separate API call - independent of filters)
  const topPerformerOfConfiguredMonth = useMemo((): TopPerformer | null => {
    if (monthReviews.length === 0) return null;

    // Group reviews by employee and calculate average rating per employee
    const employeeRatings: Record<string, { sum: number; count: number; reviews: Review[] }> = {};

    monthReviews.forEach((review: Review) => {
      if (!review.userId) return;
      if (!employeeRatings[review.userId]) {
        employeeRatings[review.userId] = { sum: 0, count: 0, reviews: [] };
      }
      employeeRatings[review.userId].sum += review.rating || 0;
      employeeRatings[review.userId].count += 1;
      employeeRatings[review.userId].reviews.push(review);
    });

    // Find employee with highest average rating
    let topEmployeeId: string | null = null;
    let highestAverage = 0;

    Object.entries(employeeRatings).forEach(([userId, data]) => {
      const avg = data.sum / data.count;
      if (avg > highestAverage) {
        highestAverage = avg;
        topEmployeeId = userId;
      }
    });

    if (!topEmployeeId) return null;

    const topUser = employees.find((e) => e.id === topEmployeeId);
    if (!topUser) return null;

    return {
      user: topUser,
      averageRating: highestAverage,
      totalReviews: employeeRatings[topEmployeeId].count,
    };
  }, [monthReviews, employees]);

  // Get month name for display
  const configuredMonthName = useMemo(() => getMonthName(monthOffset), [monthOffset]);

  const selectedPeriodLabel = useMemo(() => {
    if (filters.selectedYear && filters.selectedMonth) {
      return `${getMonthNameByNumber(filters.selectedMonth)} ${filters.selectedYear}`;
    }

    if (filters.selectedYear) {
      return filters.selectedYear;
    }

    return configuredMonthName;
  }, [filters.selectedYear, filters.selectedMonth, configuredMonthName]);

  const displayedTopPerformer = hasSelectedPeriod
    ? topPerformerOfSelectedPeriod
    : topPerformerOfConfiguredMonth;

  if (loadingBranch || loadingEmployees || loadingReviews || loadingMonthReviews) {
    return <LoadingIndicator message="Loading ratings data..." />;
  }

  if (errorBranch || errorEmployees || errorReviews) {
    return <ErrorDisplay message="Error fetching rating data." />;
  }

  if (!currentUserIsOwnerOf(branch?.companyId)) {
    return <div>You are not allowed to view this page</div>;
  }

  return (
    <RatingResults
      branch={branch ?? null}
      companyId={companyId}
      employees={employees}
      reviews={filteredReviews}
      averageRating={averageRating}
      totalReviews={totalReviews}
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={clearFilters}
      topPerformer={displayedTopPerformer?.user ?? null}
      topPerformerRating={displayedTopPerformer?.averageRating ?? 0}
      topPerformerMonth={selectedPeriodLabel}
      companyName={company?.name ?? ''}
      periodLabel={selectedPeriodLabel}
      employeeSummary={employeeSummary}
    />
  );
};

